import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { enumPasswordLabel } from '@enum/enumPasswordLabel';
import { enumBondType, enumIdentifierQualifier } from '@data-layer/enums/common';
import { enumEntrySummaryDutiesPaymentType, enumEntrySummaryType } from '@data-layer/enums/entry-summaries';
import {
  CANADA_COUNTRY_CODE,
  CHINA_COUNTRY_CODE,
  ENTERED_VALUE_CHINA_LIMIT,
  ENTERED_VALUE_TOTAL_LIMIT,
  MEXICO_COUNTRY_CODE,
  USA_COUNTRY_CODE,
} from '@constants/constants';
import { enumFileExtension } from '@enum/enumFileExtension';
import { DateHelper } from '@helpers/date.helper';
import { UtilsHelper } from '@helpers/utils-helper';

export const getPasswordErr = (actualValue: string, passwordLabel: enumPasswordLabel): boolean => {
  let res = false;
  switch (passwordLabel) {
    case enumPasswordLabel.COUNT:
      res = actualValue.search(/^.{8,15}$/) < 0 ? true : false;
      break;
    case enumPasswordLabel.UPPER_LETTER:
      res = actualValue.search(/[A-Z]/) < 0 ? true : false;
      break;
    case enumPasswordLabel.LOWER_LETTER:
      res = actualValue.search(/[a-z]/) < 0 ? true : false;
      break;
    case enumPasswordLabel.INC_NUMBER:
      res = actualValue.search(/[0-9]/) < 0 ? true : false;
      break;
    default:
      break;
  }

  return res;
};

export const PASSWORD_REGEX = '(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])[a-zA-Z0-9!_@#$%^&]{0,}$';
export const emailPattern = new RegExp('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$', 'i');

export const CUSTOM_MASK = {
  L: {
    pattern: new RegExp('[A-Za-z0-9"\'`.,:;& -]'),
  },
  N: {
    pattern: new RegExp(/[A-Za-z. ]/),
  },
  F: {
    pattern: new RegExp(/[A-Za-z0-9!"#$%&'()+,\-.\/:;<=>?@\[\]^_`{|}~ ]/),
  },
  Q: {
    pattern: new RegExp(/[A-Za-z0-9!"#$%&'()+,\-.\/:;<=>?@\[\]^_`{|}~\n ]/),
  },
  A: {
    pattern: new RegExp(/[A-Za-z0-9 ]/),
  },
  0: {
    pattern: new RegExp(/[0-9]/),
  },
};

export const HTS_MASK = '0000.00.0000';
export const HTS_MASK_8 = '0000.00.00';
export const HTS_MASK_6 = '0000.00';

export function suretyCodeValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const REGEXP = /^([0-9]{3})?$/g;

    return REGEXP.test(value) ? null : { wrongSuretyCode: true };
  };
}

export function referenceNumberValidator(forEntry: boolean = false): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control.value;
    const type: enumBondType = control.parent && control.parent.get('type') ? control.parent.get('type').value : null;
    if (!value) {
      return type && type === enumBondType.SINGLE_TRANSACTION ? { required: true } : null;
    }
    const REGEXP = /^(\w{0,9})?$/g;

    if (!REGEXP.test(value)) {
      return { wrongReferenceNumber: true };
    }

    if (forEntry && type === enumBondType.SINGLE_TRANSACTION) {
      return /[a-zA-Z]/g.test(value) ? null : { shouldHasAtLeastOneLetter: true };
    }

    return null;
  };
}

export function identifierEINValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value: string = control.value;
    if (!value) {
      return null;
    }
    const REGEXP = /^([0-9]{9})([A-Za-z0-9]{2})?$/g;

    if (!REGEXP.test(value)) {
      return { notMatchEIN: true };
    }

    const WRONG_CHAPTERS = /[IiOoZz]/g;

    return value.search(WRONG_CHAPTERS) !== -1 ? { wrongFormatIEN: true } : null;
  };
}

export function identifierSSNValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const REGEXP = /^([0-9]{9})$/g;
    return REGEXP.test(value) ? null : { notMatchSSN: true };
  };
}

export function identifierANIValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value: string = control.value;
    if (!value) {
      return null;
    }
    const REGEXP = /^([0-9]{11})?$/g;

    if (!REGEXP.test(value)) {
      return { notMatchANI: true };
    }

    const WRONG_CHAPTERS = /[IiOoZz]/g;

    return value.search(WRONG_CHAPTERS) !== -1 ? { wrongFormatANI: true } : null;
  };
}

export class CustomValidator {
  public static entryDutiesPaymentType(control: AbstractControl): ValidationErrors | null {
    const value: enumEntrySummaryDutiesPaymentType = control.value;
    if (value === enumEntrySummaryDutiesPaymentType.ACH) {
      return { achMethodNotAllowed: true };
    }
    return null;
  }

  public static matchLength(matchLength: number | number[]) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value;
      if (!value) {
        return null;
      }

      const possibleLength: number[] = Array.isArray(matchLength) ? matchLength : [matchLength];

      if (possibleLength.includes(value.length)) {
        return null;
      }

      return {
        matchLength: true,
      };
    };
  }

  public static maxFileSize(maxFileSizeMB: number = 10) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: File = control.value;
      if (!value) {
        return null;
      }

      const fileSizeMB = value.size / 1024 / 1024;

      return fileSizeMB > maxFileSizeMB
        ? { maxFileSize: { currentSize: Math.round(fileSizeMB * 100) / 100, maxFileSize: maxFileSizeMB } }
        : null;
    };
  }

  public static onlyImage(control: AbstractControl): ValidationErrors | null {
    const value: File = control.value;
    if (!value) {
      return null;
    }

    return value.type.startsWith('image') ? null : { onlyImage: true };
  }

  public static fileExtensions(allowFileExtensions: enumFileExtension[]): ValidatorFn {
    return (control: AbstractControl) => {
      const value: File = control.value;
      if (!value) {
        return null;
      }
      const type: enumFileExtension = UtilsHelper.lastElement<string>((value.name || '').split('.')).toLowerCase() as enumFileExtension;

      return allowFileExtensions.includes(type) ? null : { fileExtensionNotAllowed: true };
    };
  }

  public static notMatchValues<T>(values: T[], errorMessage: string = 'CAN_NOT_BE_SAME_AS'): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const value: T = control.value;
      if (!value || !values || values.length < 1) {
        return null;
      }

      if (values.includes(value)) {
        return { [errorMessage]: true };
      }
    };
  }

  public static shouldMatchSCACValue(scac: string, errorMessage: string = 'shipmentBLMatchSCAC'): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value || !scac) {
        return null;
      }

      if (scac.toUpperCase() !== control.value.toUpperCase()) {
        return { [errorMessage]: { value: scac, type: scac.length === 3 ? 'Airline Prefix' : 'SCAC' } };
      }
    };
  }

  public static requiredOneOfControls(controlNameList: string[], errorMessage: string = 'requiredOneOfControls') {
    return (fg: UntypedFormGroup): { [key: string]: any } => {
      const controlList = controlNameList.map((name) => fg.get(name));
      const hasOne = controlList.some((control) => !UtilsHelper.isNullOrUndefined(control.value) && control.value !== '');

      for (const control of controlList) {
        if ((hasOne && control.invalid) || (!hasOne && control.valid)) {
          control.updateValueAndValidity();
        }
      }

      if (hasOne) {
        return null;
      }

      return { [errorMessage]: true };
    };
  }

  public static requiredOneOfControlsChild(controlNameList: string[], errorMessage: string = 'requiredOneOfControlsChild') {
    return (curControl: UntypedFormControl): { [key: string]: any } => {
      const fg = curControl.parent;
      const controlList = controlNameList.map((name) => fg.get(name));
      const hasOne = controlList.some((control) => !UtilsHelper.isNullOrUndefined(control.value) && control.value !== '');

      if (hasOne) {
        return null;
      }

      return { [errorMessage]: true };
    };
  }

  public static requiredDateOfBirthIfSNN(fieldName: string = 'identifierQualifier', errorMessage: string = 'required') {
    return (control: UntypedFormControl): { [key: string]: any } => {
      if (!control || !control.parent) {
        return null;
      }
      const identifierQualifier = control.parent.get(fieldName);
      if (identifierQualifier.value === enumIdentifierQualifier.SSN && !control.value) {
        return {
          [errorMessage]: true,
        };
      }
      return null;
    };
  }

  public static requiredField(fieldName: string, errorMessage: string = 'required') {
    return (group: UntypedFormGroup): { [key: string]: any } => {
      const control = group.get(fieldName);
      if (!control || !control.value) {
        return {
          [errorMessage]: true,
        };
      }
      return null;
    };
  }

  public static requiredHTSCountryOfOrigin(relatedFieldName: string, errorMessage: string = 'required') {
    return (control: UntypedFormControl): { [key: string]: any } => {
      if (!control.parent || !control.parent.get(relatedFieldName)) {
        return null;
      }
      const relatedControl = control.parent.get(relatedFieldName);
      const isEmptyValue: boolean = UtilsHelper.isNullOrUndefined(control.value) || control.value === '';
      const isEmptyRelatedValue: boolean = UtilsHelper.isNullOrUndefined(relatedControl.value) || relatedControl.value === '';

      if (isEmptyRelatedValue && !isEmptyValue && relatedControl.valid) {
        relatedControl.updateValueAndValidity();
      }
      if (isEmptyValue && !isEmptyRelatedValue) {
        return {
          [errorMessage]: true,
        };
      }
      return null;
    };
  }

  public static lineItemEnteredValue(
    type: enumEntrySummaryType,
    totalValue: number,
    lineItemRate: number,
    existedCooCodes: string[],
    isAniImporter: boolean = false,
  ) {
    return (control: UntypedFormControl): { [key: string]: any } => {
      if (!control || !control.value) {
        return null;
      }
      const countryOfOriginCode: UntypedFormControl = (control.parent.get(['countryOfOrigin']) as UntypedFormControl)?.value?.code;
      const isHasCN: boolean = [...existedCooCodes, countryOfOriginCode].some((code) => code === CHINA_COUNTRY_CODE);
      const totalLimit: number = isHasCN ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
      const value: number = +control.value;
      const total: number = totalValue + value * lineItemRate;
      if (type === enumEntrySummaryType.FORMAL && !isAniImporter && total <= totalLimit) {
        return {
          [isHasCN && total < ENTERED_VALUE_CHINA_LIMIT ? 'formalTypeCnEnteredValueTooSmall' : 'formalTypeEnteredValueTooSmall']: {
            actual: total,
            type: enumEntrySummaryType[type],
            limit: totalLimit,
          },
        };
      }
      if (type === enumEntrySummaryType.INFORMAL && total > totalLimit) {
        return {
          [isHasCN && total > ENTERED_VALUE_CHINA_LIMIT ? 'informalTypeCnEnteredValueTooBig' : 'informalTypeEnteredValueTooBig']: {
            actual: total,
            type: enumEntrySummaryType[type],
            limit: totalLimit,
          },
        };
      }
      return null;
    };
  }

  public static shipmentTypeEnteredValue(totalValue: number, existedCooCodes: string[], isAniImporter: boolean = false) {
    return (control: UntypedFormControl): { [key: string]: any } => {
      if (!control || !control.value) {
        return null;
      }
      const isHasCN: boolean = existedCooCodes.some((code) => code === CHINA_COUNTRY_CODE);
      const totalLimit: number = isHasCN ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
      const value: enumEntrySummaryType = control.value;
      if (value === enumEntrySummaryType.FORMAL && !isAniImporter && totalValue <= totalLimit) {
        return {
          [isHasCN && totalValue <= ENTERED_VALUE_CHINA_LIMIT ? 'formalTypeCnEnteredValueTooSmall' : 'formalTypeEnteredValueTooSmall']: {
            actual: totalValue,
            type: enumEntrySummaryType[value],
            limit: totalLimit,
          },
        };
      }
      if (value === enumEntrySummaryType.INFORMAL && totalValue > totalLimit) {
        return {
          [isHasCN && totalValue > ENTERED_VALUE_TOTAL_LIMIT ? 'informalTypeCnEnteredValueTooBig' : 'informalTypeEnteredValueTooBig']: {
            actual: totalValue,
            type: enumEntrySummaryType[value],
            limit: totalLimit,
          },
        };
      }
      return null;
    };
  }

  public static updateControl(controlName: string) {
    return (control: UntypedFormControl): { [key: string]: any } => {
      const relativeControl: UntypedFormControl = control.parent ? (control.parent.get(controlName) as UntypedFormControl) : null;
      if (relativeControl) {
        relativeControl.updateValueAndValidity();
      }
      return null;
    };
  }

  public static minDate(date: string) {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control || !control.value) {
        return null;
      }
      const dateTime: number = date ? DateHelper.getTime(date) : null;
      const valueTime: number = control.value ? DateHelper.getTime(control.value) : null;
      if (dateTime > valueTime) {
        return {
          minDate: true,
        };
      }
      return null;
    };
  }

  public static shouldNotMatchBillNumber(relatedControlName: string[], errorMessage: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const value = control.value;
      if (!value) {
        return null;
      }
      const shipmentControl = control.parent.parent;
      if (!shipmentControl) {
        return null;
      }
      const relatedControl = shipmentControl.get(relatedControlName);
      if (!relatedControl) {
        return null;
      }
      const relatedControlValue = relatedControl.value;
      if (!relatedControlValue) {
        return null;
      }

      const isInvalid = value === relatedControlValue;
      setTimeout(() => {
        if (isInvalid !== relatedControl.invalid) {
          relatedControl.updateValueAndValidity();
        }
      }, 0);

      if (isInvalid) {
        return { [errorMessage]: relatedControlName };
      }
    };
  }

  public static shouldBLBeRequiredNumber(relatedControlName: string[], isRail: boolean, errorMessage: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const value = control.value;
      if (!control || isRail) {
        return null;
      }

      const shipmentControl = control.parent.parent;
      if (!shipmentControl) {
        return null;
      }

      const relatedControl = shipmentControl.get(relatedControlName);
      if (!relatedControl) {
        return null;
      }

      const relatedControlValue = relatedControl.value;

      const isInvalid = !value?.length && !!relatedControlValue?.length;
      const isRelatedInvalid = !!value?.length && !relatedControlValue?.length;

      setTimeout(() => {
        if (isRelatedInvalid !== relatedControl.invalid) {
          relatedControl.updateValueAndValidity();
        }
      }, 0);

      if (isInvalid) {
        return { [errorMessage]: relatedControlName };
      }
    };
  }

  public static notMatchRelatedHtsControl({
    relatedControlName,
    errorMessage,
    relatedControlErrorMessage,
  }: {
    relatedControlName: string | string[];
    errorMessage: string;
    relatedControlErrorMessage: string;
  }): (control: AbstractControl) => { [key: string]: any } {
    return (control: AbstractControl): { [key: string]: any } => {
      const value = control.value;

      const itemForm = control.parent && control.parent.parent ? control.parent.parent : undefined;
      if (!itemForm) {
        return null;
      }
      const relatedControl = itemForm.get(relatedControlName);
      if (!relatedControl) {
        return null;
      }
      const relatedControlValue = relatedControl.value;

      const relatedHasError = relatedControl.getError(relatedControlErrorMessage);

      if (!value) {
        if (relatedHasError) {
          setTimeout(() => {
            relatedControl.updateValueAndValidity();
          }, 100);
        }
        return null;
      }
      if (!relatedControlValue) {
        return null;
      }
      if (value !== relatedControlValue && relatedHasError) {
        setTimeout(() => {
          relatedControl.updateValueAndValidity();
        }, 100);
      } else if (value === relatedControlValue && !relatedHasError) {
        setTimeout(() => {
          relatedControl.updateValueAndValidity();
        }, 100);
      }

      if (value === relatedControlValue) {
        return { [errorMessage]: relatedControlName };
      }
      return null;
    };
  }

  public static stateIsRequired(fieldName: string = 'country', errorMessage: string = 'required') {
    return (control: UntypedFormControl): { [key: string]: any } => {
      if (!control || !control.parent) {
        return null;
      }

      const country: AbstractControl = control.parent.get(fieldName);

      if ([CANADA_COUNTRY_CODE, USA_COUNTRY_CODE, MEXICO_COUNTRY_CODE].includes(country.value?.code) && !control.value) {
        return {
          [errorMessage]: true,
        };
      }
      return null;
    };
  }

  public static notUndefined(): (control: AbstractControl) => { [key: string]: any } {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control) {
        return null;
      }
      const value: number = control.value;

      if (value === undefined) {
        return {
          required: true,
        };
      }
      return null;
    };
  }
}

export function notTheSameAsValidator(relatedControlName: string, errorMessage: string = 'CAN_NOT_BE_SAME_AS'): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const parent = control.parent;
    if (!parent) {
      return null;
    }
    const relatedControl = parent.get(relatedControlName);
    if (!relatedControl) {
      return null;
    }
    const relatedControlValue = relatedControl.value;
    if (!relatedControlValue) {
      return null;
    }

    const isInvalid = value === relatedControlValue;
    setTimeout(() => {
      if (isInvalid !== relatedControl.invalid) {
        relatedControl.updateValueAndValidity();
      }
    }, 0);

    if (isInvalid) {
      return { [errorMessage]: relatedControlName };
    }
  };
}

export function hazardousGoodsClaimedValidator(control: UntypedFormControl): { noHazardousGoodsClaimed: boolean } {
  if (!control.value) {
    return {
      noHazardousGoodsClaimed: true,
    };
  } else {
    return null;
  }
}
